import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import axios from 'axios';

import {createDataApi} from '../api/dataApi';
import {createDefinitionApi} from '../api/definitionApi';
import {createExportApi} from '../api/exportApi';
import {createPresetApi} from '../api/presetApi';
import {createShareApi} from '../api/shareApi';
import {createSuggestionApi} from '../api/suggestionApi';
import type {ApiContext} from '../api/types';
import {useDataGridErrorContext} from '../context/useDataGridErrorContext';
import {useEagGridStore} from '../store/useEagGridStore';

// TODO: integration test coverage — useHttpCalls is a thin axios wrapper with 26 methods split
// across domain API factories. Each method is trivially testable via integration tests (MSW).
// Low unit-test value-to-effort ratio.
// See https://carvago.atlassian.net/browse/T20-112912 for tracking.

export type UseHttpCalls = {
  gridCode?: string;
  presetInstanceId?: string;
  onApiError?: (error: FetchBaseQueryError) => void;
};

export const useHttpCalls = (settings?: UseHttpCalls) => {
  // Store reference is stable — read static config values directly from store state rather than
  // subscribing to context. This avoids re-renders when context changes (Rule 5.2: don't
  // subscribe to state only used in callbacks / axios closures, not in render output).
  const store = useEagGridStore();
  const errorContext = useDataGridErrorContext();

  const handleApiError = (error: unknown) => {
    // Silently ignore cancelled requests — these are expected when a newer fetchPage
    // aborts an in-flight request via AbortController.signal.
    if (axios.isCancel(error)) {
      return;
    }

    const response =
      typeof error === 'object' && error !== null && 'response' in error
        ? (error as {response: FetchBaseQueryError}).response
        : undefined;

    if (response) {
      settings?.onApiError?.(response);
      errorContext?.onApiError(response);
    }
  };

  const storeState = store.getState();
  const gridCode = settings?.gridCode ?? storeState.gridCode;
  const presetInstanceId = settings?.presetInstanceId ?? storeState.presetInstanceId;

  const ctx: ApiContext = {gridCode, presetInstanceId, handleApiError};

  // Each create*Api call returns a plain object of axios-based methods.
  // The React Compiler auto-memoizes this merged result when ctx inputs are stable.
  // DO NOT wrap in useMemo — the compiler handles this.
  return {
    ...createPresetApi(ctx),
    ...createDefinitionApi(ctx),
    ...createDataApi(ctx),
    ...createSuggestionApi(ctx),
    ...createExportApi(ctx),
    ...createShareApi(ctx),
  };
};
